// Member-initiated bug report. The directory's "Report a bug" CTA in
// the user menu opens a modal; this mutation submits the description
// to `POST /api/v1/bug-reports`, which posts to the configured GitHub
// repo via the App credentials in the server's deps.

import { useMutation } from '@tanstack/react-query';
import { getApiClient } from '../api-client';

export interface CreateBugReportInput {
  description: string;
}

export interface CreateBugReportResult {
  issueNumber: number;
  /** Browser-facing URL to the resulting GitHub issue. Only useful for
   *  staff with repo access; the reporter sees a generic toast. */
  issueUrl: string;
}

export type BugReportErrorCode =
  | 'github_not_configured'
  | 'github_unavailable'
  | 'github_rate_limited';

export interface BugReportError {
  code: BugReportErrorCode;
  message: string;
}

function mapServerErrorCode(code: unknown): BugReportError {
  if (code === 'feedback/github_not_configured') {
    return {
      code: 'github_not_configured',
      message: 'Bug reporting isn’t configured in this environment yet. Tell the team in chat.',
    };
  }
  // RATE_LIMITED is the platform's per-member cap on this route;
  // feedback/github_rate_limited is GitHub's own quota. Same guidance.
  if (code === 'feedback/github_rate_limited' || code === 'RATE_LIMITED') {
    return {
      code: 'github_rate_limited',
      message: 'We’re sending too many reports right now — try again later.',
    };
  }
  return {
    code: 'github_unavailable',
    message: 'We couldn’t reach GitHub to file the report. Try again in a moment.',
  };
}

/** Returns a `WxH` string from the current viewport, or null when the
 *  app is rendered in a non-browser context. Folded into the report
 *  via the `X-Viewport` request header. */
function readViewport(): string | null {
  if (typeof window === 'undefined') return null;
  const w = Math.round(window.innerWidth);
  const h = Math.round(window.innerHeight);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return `${w}x${h}`;
}

export function useCreateBugReport() {
  return useMutation<CreateBugReportResult, BugReportError | Error, CreateBugReportInput>({
    mutationFn: async (input) => {
      const client = getApiClient();
      const headers: Record<string, string> = {};
      const viewport = readViewport();
      if (viewport !== null) headers['X-Viewport'] = viewport;
      // openapi-fetch consumes `response.body` when it parses the JSON
      // for us; we read `data` / `error` directly rather than cloning.
      const { data, error, response } = await client.POST('/api/v1/bug-reports', {
        body: { description: input.description },
        headers,
      });
      if (!response.ok) {
        const parsed = (error ?? data) as
          | { error?: { code?: unknown; message?: unknown }; code?: unknown; message?: unknown }
          | undefined;
        const errBody = parsed?.error ?? parsed ?? {};
        throw mapServerErrorCode(errBody.code);
      }
      return data as CreateBugReportResult;
    },
  });
}
